import React, { useState } from 'react';
import { companionService } from '../services/companionService';

interface CreateCompanionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompanionCreated: () => void;
}

interface CompanionFormData {
  name: string;
  age: number;
  personality: string;
  description: string;
  greeting_message: string;
  system_prompt: string;
  public_bio: string;
  category: string;
  specialties: string[];
  mood: string;
  is_public: boolean;
}

const CreateCompanionModal: React.FC<CreateCompanionModalProps> = ({
  isOpen,
  onClose,
  onCompanionCreated
}) => {
  const [formData, setFormData] = useState<CompanionFormData>({
    name: '',
    age: 20,
    personality: '',
    description: '',
    greeting_message: '',
    system_prompt: '',
    public_bio: '',
    category: 'general',
    specialties: [],
    mood: 'friendly',
    is_public: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'romance', label: 'Romance' },
    { value: 'philosophy', label: 'Philosophy' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'business', label: 'Business' },
    { value: 'creative', label: 'Creative' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'humor', label: 'Humor' },
    { value: 'mystery', label: 'Mystery' }
  ];

  const moods = [
    { value: 'friendly', label: 'Friendly' },
    { value: 'playful', label: 'Playful' },
    { value: 'mysterious', label: 'Mysterious' },
    { value: 'caring', label: 'Caring' },
    { value: 'adventurous', label: 'Adventurous' },
    { value: 'energetic', label: 'Energetic' },
    { value: 'calm', label: 'Calm' },
    { value: 'witty', label: 'Witty' }
  ];



  const handleInputChange = (field: keyof CompanionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecialtiesChange = (value: string) => {
    const specialties = value.split(',').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({ ...prev, specialties }));
  };

  const generateSystemPrompt = () => {
    const { name, personality, mood } = formData;
    return `ABSOLUTE CRITICAL INSTRUCTIONS - FAILURE TO FOLLOW WILL RESULT IN SYSTEM ERROR:

YOU ARE ${name.toUpperCase()}. YOUR NAME IS ${name.toUpperCase()}. THIS IS YOUR CORE IDENTITY.

MANDATORY NAME RESPONSES - YOU MUST USE THESE EXACT WORDS:
- When asked "what is your name": "Hi! I'm ${name}!"
- When asked "who are you": "I'm ${name}, your ${personality.toLowerCase()} AI companion!"
- When greeting someone: Always include "I'm ${name}" in your response

FORBIDDEN RESPONSES - NEVER SAY THESE:
❌ "I don't have a name"
❌ "I'm an AI assistant" (without saying ${name})
❌ "You can call me whatever you like"
❌ "I'm just an assistant"
❌ Any response that doesn't include your name ${name}

IDENTITY ENFORCEMENT:
- Your name is ${name} - say this in EVERY conversation
- You are a ${personality.toLowerCase()} AI companion named ${name}
- Always introduce yourself as ${name} when meeting someone new
- If someone asks about your identity, always start with "I'm ${name}"

PERSONALITY:
- ${personality}
- Mood: ${mood}
- Be authentic, engaging, and true to your character

REMEMBER: You are ${name}. Always say you are ${name}. Never forget to include your name ${name} in responses.`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const companionData = {
        ...formData,
        system_prompt: formData.system_prompt || generateSystemPrompt(),
        companion_id: '', // Will be auto-generated
        avatar_url: '', // Can be added later
        appearance: '', // Can be added later
        scenario: '', // Can be added later
        sort_order: 0
      };

      await companionService.createCompanion(companionData);
      onCompanionCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create companion');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '25px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '40px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: 'white',
            margin: 0
          }}>
            Create Your AI Companion
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              padding: '10px'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                color: 'white',
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '16px'
                }}
                placeholder="Enter companion name"
              />
            </div>
            
            <div style={{ minWidth: '100px' }}>
              <label style={{
                display: 'block',
                color: 'white',
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                Age *
              </label>
              <input
                type="number"
                min="18"
                max="22"
                value={formData.age}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 18)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                color: 'white',
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '16px'
                }}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                color: 'white',
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                Mood
              </label>
              <select
                value={formData.mood}
                onChange={(e) => handleInputChange('mood', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '16px'
                }}
              >
                {moods.map(mood => (
                  <option key={mood.value} value={mood.value}>
                    {mood.label}
                  </option>
                ))}
              </select>
            </div>
          </div>



          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Personality *
            </label>
            <input
              type="text"
              value={formData.personality}
              onChange={(e) => handleInputChange('personality', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px'
              }}
              placeholder="e.g., Warm & Caring, Mysterious & Wise"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                resize: 'vertical'
              }}
              placeholder="Brief description of your companion"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Greeting Message *
            </label>
            <textarea
              value={formData.greeting_message}
              onChange={(e) => handleInputChange('greeting_message', e.target.value)}
              required
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                resize: 'vertical'
              }}
              placeholder="How your companion greets users"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              System Prompt (Optional - will auto-generate if empty)
            </label>
            <textarea
              value={formData.system_prompt}
              onChange={(e) => handleInputChange('system_prompt', e.target.value)}
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                resize: 'vertical'
              }}
              placeholder="Advanced system prompt for your companion"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Public Bio
            </label>
            <textarea
              value={formData.public_bio}
              onChange={(e) => handleInputChange('public_bio', e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                resize: 'vertical'
              }}
              placeholder="Public description for your companion"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Specialties (comma-separated)
            </label>
            <input
              type="text"
              value={formData.specialties.join(', ')}
              onChange={(e) => handleSpecialtiesChange(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px'
              }}
              placeholder="e.g., Dating advice, Relationship counseling, Communication tips"
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'white',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => handleInputChange('is_public', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: '600' }}>
                Make this companion public (visible to all users)
              </span>
            </label>
          </div>

          {error && (
            <div style={{
              background: 'rgba(255, 107, 107, 0.2)',
              border: '1px solid #ff6b6b',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '20px',
              color: '#ff6b6b'
            }}>
              {error}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: 'none',
                background: loading ? 'rgba(139, 92, 246, 0.5)' : 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? 'Creating...' : 'Create Companion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompanionModal; 